import type {
  OpenPositionsFragment,
  PaymentUnit,
  OrderId,
  Scenarios_ScenarioFragment,
  ScenarioTimeline_OrderFragment,
  ShipmentExtraFreight,
  ShipmentFreight,
  ShipId,
  ShipmentId,
  ShipmentStatus,
  VesselReservationFragment,
  VoyageAttributeId,
  VoyageCharterId,
} from '../graphql/types';
import { ClientId } from '../lib/clientId';
import { StrictPick } from '../lib/generics';
import { getMemoSet } from '../lib/memoize';
import {
  CargoAllocation,
  CargoEntry,
  CoaEntry,
  Entry,
  EventCategory,
  isTCOrderEntry,
  OffHireEntry,
  PortCallEntry,
  SingleEntry,
  SpotEntry,
  TCOrderEntry,
} from './entries';

export type DeleteEventAction = {
  readonly type: 'DELETE_EVENT';
  readonly eventId: ClientId;
  readonly eventType: EventCategory.Single | EventCategory.OffHire;
  readonly estimateIdOrScenario: ClientId | 'scenario';
};

/** Operations attached to a SuggestionSchedule describing the clean-slate
 * modifications that were applied to the base before optimization ran. Shaped
 * like the reducer's DeleteEventAction so the same payload can be replayed
 * through the reducer if a caller ever needs stateful deletion semantics; the
 * current apply path reads only `eventId`. */
export type CleanSlateLayerOp = DeleteEventAction;

export type CleanSlateLayer = {
  readonly ops: readonly CleanSlateLayerOp[];
};

export type ScenarioPatch<T> = {
  id: OrderId;
} & {
  type: 'create' | 'update';
  patch: Partial<T>;
  // | { type: 'delete' }
};

export type ScenarioOrderPatch = ScenarioPatch<
  Pick<
    ScenarioTimeline_OrderFragment,
    'outgoingVoyageCharterRelation' | 'orderPortCallRelationsByOrderId'
  > & {
    shipmentId: ClientId | null;
  }
>;

export type PatchShipment = StrictPick<ScenarioShipment, 'voyageAttributeId'>;
export type PatchShipmentMap = Map<ClientId, PatchShipment>;

export type ScenarioShipment = {
  clientId: ClientId;
  name: string | null;
  remarks: string | null;
  id: ShipmentId | null;
  externalShipmentId: string | null;
  externalShipmentStatus: ShipmentStatus | null;
  shipmentFreightByShipmentId: ShipmentFreight | null;
  shipmentExtraFreightsByShipmentId: ShipmentExtraFreight[] | null;
  voyageAttributeId: VoyageAttributeId | null;
};

export type ScenarioShipmentFreight = {
  id: number | null;
  clientId: ClientId;
  freightAmount: string | number;
  freightUnit: PaymentUnit;
  shipmentDbId: number | null;
};

export type ScenarioShipmentExtraFreight = {
  id: number | null;
  clientId: ClientId;
  freightAmount: string | number;
  freightUnit: PaymentUnit;
  name: string | null;
  description: string | null;
  shipmentDbId: number | null;
};

export const SCENARIO_DATA_SCHEMA_VERSION = 3 as const;
export const LATEST_MIGRATION_VERSION = 5 as const;

/**
 * Simplified vessel reservation data for EstimateTimeline storage.
 * Contains scalar fields plus simple nested user object.
 * Location/region relationships are looked up at render time.
 */
export type EstimateVesselReservation = Pick<
  VesselReservationFragment,
  | 'id'
  | 'duration'
  | 'endPortId'
  | 'endRegionId'
  | 'expireTime'
  | 'remark'
  | 'startPortId'
  | 'startRegionId'
  | 'startTime'
  | 'type'
  | 'vesselId'
  | 'userByCreatedByUserId'
>;

/**
 * Simplified open position data for EstimateTimeline storage.
 * Only contains scalar fields - nested relationships are looked up at render time.
 */
export type EstimateOpenPosition = Pick<
  OpenPositionsFragment,
  'id' | 'directionId' | 'remark' | 'startPortId' | 'startTime' | 'vesselId'
>;

export enum EntryState {
  /** Entry is updated or created in this scenario */
  UPDATED = 'U',
  /** Entry is deleted in this scenario and should remain deleted after sync or commit */
  DELETED = 'D',
}

export type EstimateTimeline = {
  clientId: ClientId;
  vesselId: ShipId;
  type: 'estimate';

  /** All TC assignments in this timeline */
  schedule: (TCOrderEntry | SingleEntry | OffHireEntry)[];
  cargoAllocations: CargoAllocation[];

  /** All port calls related to this timeline */
  portCalls: PortCallEntry[];
  patchOrders: Map<OrderId, ScenarioOrderPatch>;
  patchShipments: PatchShipmentMap;
  entryStates: Map<ClientId, EntryState>; // These mark an actual record vs. last known committed state and should be used for display purposes on the timeline
  voyageCharterByOrderId: Map<OrderId, VoyageCharterId | string | null>;

  /** Freight rate overrides for orders in this estimate timeline */
  freightRateOverridesByOrderId?: FreightRateOverridesByOrderId;

  /** Vessel reservations in this estimate timeline (simplified, without nested relationships) */
  vesselReservations?: EstimateVesselReservation[];
  /** Open positions in this estimate timeline (simplified, without nested relationships) */
  openPositions?: EstimateOpenPosition[];
  /** Shipment freight overrides for shipments in this estimate timeline */
  shipmentFreightOverridesByShipmentId?: ShipmentFreightOverridesByShipmentId;
};

/** Map of order IDs to their freight rate overrides */
export type FreightRateOverridesByOrderId = Map<number, FreightRateOverride>;
export type FreightRateOverride = {
  freight: number;
  freightUnit: PaymentUnit;
};

/** Map of shipment client IDs to their freight overrides */
export type ShipmentFreightOverridesByShipmentId = Map<
  ClientId,
  ShipmentFreightOverride
>;
export type ShipmentFreightOverride = {
  /** Freight amount. Null indicates explicitly cleared (no freight). */
  freight: number | null;
  freightUnit: PaymentUnit;
};

/**
 * Represents a scenario which may or may not be committed.
 * NOTE! Changes to this structure with extreme care. The structure gets serialized and saved into database.
 * If you update this interface, be sure that the SerializedScenario interface in backend is also updated (so integration scenarios are generated correctly).
 * If the schema is updated, change the dataSchemaVersion so that any older versions of data will not be loaded
 * and coordinate the upgrade with clients if possible so that they don't lose important work.
 */
export type CoreScenario = Pick<
  Scenarios_ScenarioFragment,
  | 'organizationId'
  | 'name'
  | 'description'
  | 'dataSchemaVersion'
  | 'createdAt'
  | 'updatedAt'
  | 'createdBy'
  | 'updatedBy'
  | 'shared'
  | 'deletedAt'
> & {
  clientId: ClientId;
  initials?: string;
  dataSchemaVersion: typeof SCENARIO_DATA_SCHEMA_VERSION;
  /** Timestamp of the last known commit according to this scenario. Can be used to protect against e.g. websocket update failures.*/
  masterCommitAt?: number | null;
  isReadOnly: boolean;
  /** Integration scenarios can be marked as "master" scenario. It is always considered to be in sync. undefined interpreted as false. */
  isIntegrationMaster?: boolean;
  integrationData?: {
    externalShipmentIdByClientId?: Record<string, string>;
    orderIdsByShipmentClientId?: Record<string, number[]>;
  } | null;

  /** Timestamp for integration scenario to reflect user local time */
  importedAt?: number | null;

  /** Scenario setting to override org config for auto update behavior. Default/undefined will respect the org setting. */
  autoUpdateFromMaster?: 'always' | 'never' | 'default';
  /** Different from updatedAt. This tracks local changes to the scenario, updatedAt is for update in the database. */
  changedAt?: number | null;

  // TODO split `schedule` in scenario into 3 so each array has only entries of a single type. instead of arrays could also be maps keyed by ClientId https://github.com/Seaber-io/frontend/pull/626#discussion_r705253661
  /** All TC assignments */
  schedule: (TCOrderEntry | SingleEntry | OffHireEntry)[];
  cargoAllocations: CargoAllocation[];

  /** All Spot assignments */
  spotAssignments: SpotEntry[];
  /** All coa assignments */
  coaAssignments: CoaEntry[];

  /** All port calls related to any type of timeline */
  portCalls: PortCallEntry[];
  patchOrders: Map<OrderId, ScenarioOrderPatch>;
  patchShipments: PatchShipmentMap;
  voyageCharterByOrderId: Map<OrderId, VoyageCharterId | string | null>;

  estimateTimelines?: EstimateTimeline[];
  orderShortlist?: { orderIds: OrderId[]; updatedAt: number };
  vesselsShortlist?: { vesselIds: ShipId[]; updatedAt: number };
  freightByShipmentClientId: Map<ClientId, ScenarioShipmentFreight>;
  extraFreightByShipmentClientId: Map<ClientId, ScenarioShipmentExtraFreight[]>;
};

/**
 * Represents a scenario which may or may not be committed.
 * NOTE! Changes to this structure with extreme care. The structure gets serialized and saved into database.
 * If you update this interface, be sure that the SerializedScenario interface in backend is also updated (so integration scenarios are generated correctly).
 * If the schema is updated, change the dataSchemaVersion so that any older versions of data will not be loaded
 * and coordinate the upgrade with clients if possible so that they don't lose important work.
 */
export type InternalUserSchedule = CoreScenario & {
  type: 'user';
  id: number | null;
  entryStates: Map<ClientId, EntryState>; // These mark an actual record vs. last known committed state and should be used for display purposes on the timeline
  migrationVersion: number;
  /** Refers to the latest commit timestamp that this scenario is synced with. Can be used to warn user about potential overwrites on commit. */
  syncedWithMasterAt?: number | null;
};

export type InternalSuggestionSchedule = Omit<InternalUserSchedule, 'type'> & {
  type: 'suggestion';
  baseScenarioId: ClientId;
  /**
   * Operations that were applied to the baseScenario to produce the schedule
   * this suggestion sits on top of. Present when the ALNS optimizer had to
   * clean-slate the base before running; consumed when comparing the suggestion
   * against the root so the root side is masked identically.
   */
  cleanSlateLayer?: CleanSlateLayer;
};

export type InternalMasterSchedule = CoreScenario & {
  type: 'master';
  isReadOnly: true;
};

export type InternalBackupSchedule = CoreScenario & {
  type: 'backup';
  backedUpAt: number;
};

export type InternalInitialSchedule = CoreScenario & {
  type: 'initial';
};

export const isEstimateScenario = (
  s: ScenarioSchedule | EstimateTimeline | null,
): s is EstimateTimeline => s?.type === 'estimate';

/** Mapping of each order id present in the scenario into the assignment entry */
export const getOrderAssignments = (
  scenario: ScenarioSchedule | EstimateTimeline,
): Map<number, CargoEntry> =>
  new Map(
    (!isEstimateScenario(scenario)
      ? [
          ...scenario.schedule.filter(isTCOrderEntry),
          ...scenario.coaAssignments,
          ...scenario.spotAssignments,
        ]
      : scenario.schedule.filter(isTCOrderEntry)
    ).map((e) => [e.orderId, e]),
  );

export const getAssignedOrderIds = (
  scenario: ScenarioSchedule,
  includedVesselIds: Set<number>,
): Set<number> =>
  new Set(
    [
      ...scenario.schedule
        .filter(isTCOrderEntry)
        .filter(
          (e) => e.vesselId !== null && includedVesselIds.has(e.vesselId),
        ),
      ...scenario.coaAssignments,
      ...scenario.spotAssignments,
    ].map((assignment) => assignment.orderId),
  );

export const getAllScenarioEntries = (scenario: ScenarioSchedule): Entry[] => [
  ...scenario.schedule,
  ...scenario.coaAssignments,
  ...scenario.spotAssignments,
  ...scenario.portCalls,
];

export const isOrderShortlisted = (
  scenario: ScenarioSchedule,
  orderId: OrderId,
): boolean => {
  return scenario.orderShortlist
    ? getMemoSet(scenario.orderShortlist.orderIds).has(orderId)
    : false;
};

export type UserSchedule = InternalUserSchedule;
export type SuggestionSchedule = InternalSuggestionSchedule;
export type MasterSchedule = InternalMasterSchedule;
export type BackupSchedule = InternalBackupSchedule;
export type InitialSchedule = InternalInitialSchedule;

export type ScenarioSchedule =
  | UserSchedule
  | SuggestionSchedule
  | MasterSchedule
  | BackupSchedule
  | InitialSchedule;

export function isEntryStateDeleted(
  scenario: Pick<UserSchedule | EstimateTimeline, 'entryStates'>,
  entryClientId: ClientId,
): boolean {
  if ('entryStates' in scenario) {
    return scenario.entryStates.get(entryClientId) === EntryState.DELETED;
  } else {
    return false;
  }
}

export function isEntryStateUpdated(
  scenario: Pick<UserSchedule | EstimateTimeline, 'entryStates'>,
  entryClientId: ClientId,
): boolean {
  if ('entryStates' in scenario) {
    return scenario.entryStates.get(entryClientId) === EntryState.UPDATED;
  } else {
    return false;
  }
}
